import { useCallback, useState } from 'preact/hooks';
import { api } from '../../core/client';
import { useLiveSocket } from '../../ws/useLiveSocket';
import { resolveFleetWsUrl } from '../../core/path';
import type { FleetOverview } from './alerting.types';

/** Live fleet status over a WebSocket (D-137).
 *
 *  **What this replaces.** Three independent polls — the shell's 30 s fleet
 *  overview, plus `/admin/sites`' server list and site list — each on its own
 *  clock. Between ticks they disagreed, so the same site read `offline` on one
 *  screen and `critical` on another at the same moment. Now one server-side
 *  computation pushes one payload to every console.
 *
 *  **Authentication.** A browser `WebSocket` cannot send an `Authorization`
 *  header, so the socket is opened with a **single-use ticket** minted by an
 *  authenticated POST. The bearer token never appears in a URL — URLs are logged
 *  by proxies, kept in history and leak via `Referer`, and the token is worth the
 *  whole API while a ticket is worth one socket for 30 seconds.
 *
 *  A fresh ticket is minted **per connect attempt**, including every reconnect,
 *  because redeeming one destroys it. */

interface TicketRes {
  ticket: string;
  expires_in_secs: number;
}

export interface UseFleetLiveResult {
  /** The latest pushed payload, or `null` before the first frame arrives. */
  data: FleetOverview | null;
  /** Socket state — surfaced so a screen can fall back to the REST read. */
  state: 'connecting' | 'connected' | 'reconnecting' | 'closed';
}

/**
 * Subscribe to fleet status.
 *
 * `enabled` should be false outside cloud mode: the endpoint is `gateway-cloud`'s,
 * so at a site this would fail every reconnect forever.
 */
export function useFleetLive(enabled = true): UseFleetLiveResult {
  const [data, setData] = useState<FleetOverview | null>(null);

  // Minted inside the resolver so it happens per ATTEMPT. Putting it in a query
  // would cache a ticket that the first redemption already destroyed, and every
  // reconnect after that would be refused.
  const resolveUrl = useCallback(async () => {
    const res = await api.post<TicketRes>('/users/v1/fleet/ws-ticket', {});
    return `${resolveFleetWsUrl()}?ticket=${encodeURIComponent(res.ticket)}`;
  }, []);

  const { state } = useLiveSocket<FleetOverview>({
    resolveUrl,
    onEvent: setData,
    enabled,
  });

  return { data, state };
}
